import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Copy, Mail, MessageCircle, Send, TriangleAlert, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { ConsentSelect } from "@/components/ConsentSelect";
// Aliased: this component already has a `reportUrl` state variable.
import { reportUrl as buildReportUrl } from "@/lib/api";
import { extractCity } from "@/lib/address";
import { SALES, SENDER, senderMissing } from "@/components/report/salesConfig";
import { buildOutreach, VARIANTS, VARIANT_LABELS } from "@/lib/outreach";
import { sendBlockers, HARD_BLOCKERS, BLOCKER_TEXT, READINESS_FIELD_LABELS } from "@/lib/sendGate";

function waLink(phone, text) {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

// Deliberately not a service pitch. The user's own words: "onlara ulaşma
// niyetimin direk iş almak gibi algılanmasını istemiyorum, firmaya değer
// katmak istiyorum" — no "I build websites", no "may I send you an offer".
// Frame this as an independent observation shared because it's useful, and
// let the report itself (comparison + why-it-matters) do the persuading.
// The close is always an open door, never an ask: "reach me at the end of
// the report, if you want" — never "may I offer you...".
const TEMPLATES = {
  tr: (b, city, sender) => {
    const name = b.name || "İşletmeniz";
    const hasSite = b.has_website;
    const emailSubject = hasSite
      ? `${name} için ücretsiz dijital görünürlük değerlendirmesi`
      : `${city}'deki ${name} için ücretsiz dijital görünürlük değerlendirmesi`;
    const emailBody = hasSite
      ? `Merhaba,\n\nBen ${sender}. Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum ve ${city} bölgesindeki firmaların Google'da ve yapay zeka aramalarında (ChatGPT, Perplexity) nasıl göründüğüne baktım. Bu sırada ${name} dikkatimi çekti.\n\nİsterseniz size bölgenizdeki diğer firmalarla karşılaştırmalı, ücretsiz bir değerlendirme hazırlayabilirim — satış teklifi değil, faydalı olabileceğini düşündüğüm bir bilgi.\n\nİlgilenirseniz bana ulaşabilirsiniz.\n\nSaygılarımla,\n${sender}`
      : `Merhaba,\n\nBen ${sender}. ${city} bölgesindeki ${name} işletmesinin henüz bir web sitesi olmadığını fark ettim. Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum — Google'da ve yapay zeka aramalarında üst sıralarda olan işletmeler genelde neredeyse hiç çaba harcamadan yeni müşteri kazanıyor.\n\nİsterseniz şu an nerede durduğunuzu gösteren ücretsiz bir değerlendirme hazırlayabilirim. Satış teklifi değil — ilgilenirseniz bana ulaşabilirsiniz.\n\nSaygılarımla,\n${sender}`;
    const wa = hasSite
      ? `Merhaba ${name}! ${city} bölgesindeki yerel işletmelerin dijital görünürlüğüyle ilgileniyorum, bu sırada işletmeniz dikkatimi çekti. İsterseniz Google'da ve yapay zeka aramalarında bölgenizdeki diğer firmalarla nasıl karşılaştığınızı gösteren ücretsiz bir değerlendirme hazırlayabilirim. Satış teklifi değil, sadece ilginizi çekebilir diye düşündüm.`
      : `Merhaba ${name}! ${city} bölgesinde web sitenizin olmadığını fark ettim. Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum — isterseniz şu an Google'da ve yapay zeka aramalarında nerede durduğunuzu gösteren ücretsiz bir değerlendirme hazırlayabilirim. Satış teklifi değil, sadece ilginizi çekebilir diye düşündüm.`;
    return { emailSubject, emailBody, wa };
  },
  en: (b, city, sender) => {
    const name = b.name || "your business";
    const hasSite = b.has_website;
    const emailSubject = hasSite
      ? `A free visibility assessment for ${name}`
      : `${name} in ${city}: a free online-visibility assessment`;
    const emailBody = hasSite
      ? `Hello,\n\nMy name is ${sender}. I look into the online visibility of local businesses and checked how businesses in ${city} perform on Google and in AI search (ChatGPT, Perplexity). ${name} caught my attention.\n\nI'd be glad to put together a free assessment comparing you to other businesses in your sector locally — not a sales pitch, just information I thought could be useful.\n\nFeel free to reach out if you're interested.\n\nBest regards,\n${sender}`
      : `Hello,\n\nMy name is ${sender}. I noticed that ${name} in ${city} doesn't have its own website yet. I look into the online visibility of local businesses — those that rank higher on Google and in AI search often win new customers almost effortlessly.\n\nI'd be glad to put together a free assessment of where you currently stand. Not a sales pitch — feel free to reach out if you're interested.\n\nBest regards,\n${sender}`;
    const wa = hasSite
      ? `Hi ${name}! I look into the online visibility of local businesses in ${city} — your business caught my attention. I'd be glad to put together a free assessment of how you compare to others in your sector on Google and in AI search. Not a sales pitch, just thought it might interest you.`
      : `Hi ${name}! I noticed you don't have a website in ${city} yet. I look into the online visibility of local businesses — happy to show you, for free, where you currently stand on Google and in AI search. Not a sales pitch, just thought it might interest you.`;
    return { emailSubject, emailBody, wa };
  },
};

// The single most concrete, curiosity-driving line available: a real legal/
// image-licensing signal from THIS business's own site, not a generic
// "we can improve your SEO" claim. This is what the user asked to lean on —
// "yasal uyumluluğun ve fotoların telif kontrolü dikkat çeker" — but it stays
// inside the existing non-pitch framing (an observation, never an assertion
// of a violation; see legal.py's own "please check" wording convention).
// Only fires for businesses with a site (no website = no legal check ran)
// and honestly reflects `business.legal.issues`, which is the same data the
// free report's legal_teaser count (server.py) is built from — so whatever
// this line claims, the linked report actually backs up.
function legalHookLine(business, lang) {
  const issues = business?.legal?.issues || [];
  if (!business?.has_website || !issues.length) return "";
  const n = issues.length;
  const hasImageFlag = issues.some((i) => i.code === "stock_images_detected");
  const LINES = {
    de: hasImageFlag
      ? ` Besonders aufgefallen ist mir dabei ein Hinweis auf möglicherweise nicht lizenzierte Bilder auf der Website — einer von insgesamt ${n} rechtlichen Punkten, die ich mir angesehen habe und die in Deutschland häufig zu Abmahnungen führen.`
      : ` Dabei sind mir ${n === 1 ? "1 rechtlicher Punkt" : `${n} rechtliche Punkte`} aufgefallen — Themen, die in Deutschland häufig zu Abmahnungen führen.`,
    tr: hasImageFlag
      ? ` Bu sırada özellikle dikkatimi çeken bir şey oldu: sitede lisanssız olabilecek görsellere dair bir işaret — baktığım, Almanya'da sık sık ihtarnameye (Abmahnung) yol açan toplam ${n} hukuki noktadan biri bu.`
      : ` Bu sırada, Almanya'da sık sık ihtarnameye (Abmahnung) yol açan türden ${n === 1 ? "1 hukuki nokta" : `${n} hukuki nokta`} dikkatimi çekti.`,
    en: hasImageFlag
      ? ` One thing stood out: a hint that some images on the site may not be properly licensed — one of ${n} legal points I looked at, the kind that often leads to formal warnings (Abmahnungen) in Germany.`
      : ` ${n === 1 ? "One legal point" : `${n} legal points`} stood out — the kind that often leads to formal warnings (Abmahnungen) in Germany.`,
  };
  return LINES[lang] || LINES.de;
}

// Used once a digital-presence audit exists: leads with the score, the
// cohort benchmark, and a link to the full (German) report instead of a
// pitch. Same non-salesy framing as TEMPLATES above — the score+comparison
// is the value being given, the report explains why it matters, and the
// close is an open door ("reach me at the end of the report"), never an ask.
// `legalHook` (see legalHookLine above) is the one place this leans on a
// specific, attention-grabbing detail rather than a generic claim.
const AUDIT_TEMPLATES = {
  tr: (b, city, sender, benchLine, reportUrl, legalHook = "") => {
    const name = b.name || "İşletmeniz";
    const score = b.digital_score;
    const hasSite = b.has_website;
    const emailSubject = `Dijital görünürlüğünüz: 100 üzerinden ${score} (ücretsiz değerlendirme)`;
    const emailBody = hasSite
      ? `Merhaba,\n\nBen ${sender}. Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum ve ${city} bölgesindeki firmaların Google'da ve yapay zeka aramalarında nasıl göründüğüne baktım.\n\n${name} şu an 100 üzerinden ${score} puanda${benchLine}.${legalHook} Bölgenizdeki en görünür firmalarla anonim karşılaştırmayı da içeren ücretsiz, detaylı analizi buradan görebilirsiniz:\n${reportUrl}\n\nBu bir satış teklifi değil, faydalı olabileceğini düşündüğüm bir değerlendirme: Google'da ve yapay zeka aramalarında üst sıralarda olan işletmeler genelde neredeyse hiç çaba harcamadan yeni müşteri kazanıyor. Sorularınız olursa raporun sonunda bana ulaşabilirsiniz.\n\nSaygılarımla,\n${sender}`
      : `Merhaba,\n\nBen ${sender}. Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum ve ${city} bölgesindeki firmaların Google'da ve yapay zeka aramalarında nasıl göründüğüne baktım.\n\n${name} şu an 100 üzerinden ${score} puanda${benchLine}, bunun bir nedeni de henüz bir web sitenizin olmaması. Ücretsiz, detaylı analizi buradan görebilirsiniz:\n${reportUrl}\n\nBu bir satış teklifi değil, faydalı olabileceğini düşündüğüm bir değerlendirme. Sorularınız olursa raporun sonunda bana ulaşabilirsiniz.\n\nSaygılarımla,\n${sender}`;
    const wa = hasSite
      ? `Merhaba ${name}! Yerel işletmelerin dijital görünürlüğüyle ilgileniyorum, ${city} bölgesindeki firmaların Google'da ve yapay zeka aramalarında nasıl göründüğüne baktım. Puanınız: 100 üzerinden ${score}${benchLine}.${legalHook} Ücretsiz detaylı analizi buradan görebilirsiniz: ${reportUrl}\n\nSatış teklifi değil — faydalı olabileceğini düşündüğüm bir değerlendirme. Sorunuz olursa raporun sonunda bana ulaşabilirsiniz.`
      : `Merhaba ${name}! ${city} bölgesinde web sitenizin olmadığını fark ettim. Dijital görünürlüğünüz 100 üzerinden ${score}${benchLine}. Ücretsiz detaylı analizi buradan görebilirsiniz: ${reportUrl}\n\nSatış teklifi değil — faydalı olabileceğini düşündüğüm bir değerlendirme. Sorunuz olursa raporun sonunda bana ulaşabilirsiniz.`;
    return { emailSubject, emailBody, wa };
  },
  en: (b, city, sender, benchLine, reportUrl, legalHook = "") => {
    const name = b.name || "your business";
    const score = b.digital_score;
    const hasSite = b.has_website;
    const emailSubject = `Your digital visibility: ${score}/100 (free assessment)`;
    const emailBody = hasSite
      ? `Hello,\n\nMy name is ${sender}. I look into the online visibility of local businesses and checked how businesses in ${city} perform on Google and in AI search.\n\n${name} currently scores ${score}/100${benchLine}.${legalHook} The full, free analysis — including an anonymous comparison with the most visible businesses in your sector locally — is here:\n${reportUrl}\n\nThis isn't a sales pitch, just an assessment I thought could be useful: businesses that rank higher on Google and in AI search often win new customers almost effortlessly. If you have questions, you can reach me at the end of the report.\n\nBest regards,\n${sender}`
      : `Hello,\n\nMy name is ${sender}. I look into the online visibility of local businesses and checked how businesses in ${city} perform on Google and in AI search.\n\n${name} currently scores ${score}/100${benchLine}, partly because there's no website yet. The full, free analysis is here:\n${reportUrl}\n\nThis isn't a sales pitch, just an assessment I thought could be useful. If you have questions, you can reach me at the end of the report.\n\nBest regards,\n${sender}`;
    const wa = hasSite
      ? `Hi ${name}! I look into the online visibility of businesses in ${city} and checked how they perform on Google and in AI search. Your score: ${score}/100${benchLine}.${legalHook} The full, free analysis is here: ${reportUrl}\n\nNot a sales pitch — just an assessment I thought could be useful. Questions? You can reach me at the end of the report.`
      : `Hi ${name}! I noticed you don't have a website in ${city} yet. Your digital visibility score is ${score}/100${benchLine}. The full, free analysis is here: ${reportUrl}\n\nNot a sales pitch — just an assessment I thought could be useful. Questions? You can reach me at the end of the report.`;
    return { emailSubject, emailBody, wa };
  },
};

// Prefer the top-3 average over the cohort median: a business sitting just
// under the median ("69 vs 68") reads as unremarkable, while "69 vs the
// top performers' 80" is the actual, attention-grabbing gap the report page
// itself leads with. Only falls back to the median if top-3 isn't available.
const BENCH_LINE_TOP3 = {
  de: (avg) => (avg != null ? ` — Durchschnitt der Top-3-Unternehmen: ${avg}` : ""),
  tr: (avg) => (avg != null ? `, ilk 3 firmanın ortalaması ${avg}` : ""),
  en: (avg) => (avg != null ? ` — average of the top-3 businesses: ${avg}` : ""),
};
const BENCH_LINE_MEDIAN = {
  de: (median) => (median != null ? ` — Durchschnitt vergleichbarer Unternehmen: ${median}` : ""),
  tr: (median) => (median != null ? `, bölgedeki firmaların ortalaması ${median}` : ""),
  en: (median) => (median != null ? ` — average of comparable businesses: ${median}` : ""),
};

export const MessageDialog = ({
  business, benchmark, open, onOpenChange, onEnsureReport, onCompleteReport, onConsentChange,
}) => {
  const [lang, setLang] = useState("de");
  const [sender, setSender] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("leadharita_sender");
    if (saved) setSender(saved);
    else if (SENDER.name) setSender(SENDER.name);
  }, []);

  const onSenderChange = (v) => {
    setSender(v);
    localStorage.setItem("leadharita_sender", v);
  };

  const city = useMemo(() => extractCity(business?.address), [business]);
  const senderName = sender.trim();

  const hasAudit = business?.digital_score != null;
  const fallbackUrl = buildReportUrl(business?.report_token);
  const [reportUrl, setReportUrl] = useState(fallbackUrl);
  const [top3, setTop3] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [estimatedCost, setEstimatedCost] = useState(null);
  const [override, setOverride] = useState(false);
  const [kind, setKind] = useState("first"); // first | noOpen | opened
  const [variantChoice, setVariantChoice] = useState(null); // null = automatic
  const [reportLoading, setReportLoading] = useState(false);

  // Always call onEnsureReport when an audited business's dialog opens —
  // even if a report_token already exists — because the top-3 comparison
  // (needed for the message's benchmark line) is computed fresh server-side
  // and isn't stored on the business record. The endpoint is idempotent, so
  // re-requesting an existing token costs one cheap round trip, not a redo.
  useEffect(() => {
    if (!business || !open) {
      setReportLoading(false);
      return;
    }
    setEstimatedCost(null);
    if (!hasAudit || !onEnsureReport) {
      setReportLoading(false);
      setReportUrl(null);
      setTop3(null);
      setReadiness(null);
      return;
    }
    let cancelled = false;
    setReportLoading(true);
    onEnsureReport(business)
      .then(({ url, top3: t3, readiness: r, estimatedCostUsd }) => {
        if (cancelled) return;
        setReportUrl(url);
        setTop3(t3 || null);
        setReadiness(r || null);
        setEstimatedCost(estimatedCostUsd ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setReportUrl(fallbackUrl);
        setTop3(null);
        setReadiness(null);
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });
    return () => {
      cancelled = true;
      setReportLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, open]);

  // Per-dialog state resets are keyed on the business's identity, not its
  // object: completeNow / ConsentSelect replace the object and must not wipe
  // the user's choices.
  const placeId = business?.place_id ?? null;
  // Bumped whenever the dialog switches business / opens / closes, so an
  // in-flight completeNow can tell it is stale (works even without an id).
  const dialogGen = useRef(0);
  useEffect(() => {
    dialogGen.current += 1;
    if (!open) return;
    setOverride(false);
    setKind("first");
    setVariantChoice(null);
  }, [placeId, open]);

  const kit = useMemo(() => {
    if (!business || lang !== "de" || !hasAudit || !reportUrl) return null;
    return buildOutreach({
      business, city, sender: sender.trim(), top3, benchmark, reportUrl,
      price: `${SALES.priceEur} €`, paymentLive: SALES.paymentLive, variant: variantChoice,
    });
  }, [business, lang, hasAudit, reportUrl, city, sender, top3, benchmark, variantChoice]);

  const tpl = useMemo(() => {
    if (!business) return { emailSubject: "", emailBody: "", wa: "" };
    if (kit) {
      const m = kind === "noOpen" ? kit.followupNoOpen : kind === "opened" ? kit.followupOpened : kit.first;
      return { emailSubject: m.emailSubject, emailBody: m.emailBody, wa: m.wa };
    }
    // German never uses the legacy templates (formal salutation, no opt-out):
    // without a kit there is nothing to show and sending is hard-blocked (no_kit).
    if (lang === "de") return { emailSubject: "", emailBody: "", wa: "" };
    const cityOrDefault = city || "Ihrer Stadt";
    if (hasAudit && reportUrl) {
      const avg3 = top3?.length
        ? Math.round(top3.reduce((s, t) => s + t.score, 0) / top3.length)
        : null;
      const benchLine = avg3 != null
        ? BENCH_LINE_TOP3[lang](avg3)
        : BENCH_LINE_MEDIAN[lang](benchmark?.score_median);
      const legalHook = legalHookLine(business, lang);
      return AUDIT_TEMPLATES[lang](business, cityOrDefault, senderName, benchLine, reportUrl, legalHook);
    }
    if (hasAudit && reportLoading) {
      return { emailSubject: "", emailBody: "", wa: "" }; // shown as a loading state below
    }
    return TEMPLATES[lang](business, cityOrDefault, senderName);
  }, [business, lang, city, senderName, hasAudit, reportUrl, reportLoading, top3, benchmark, kit, kind]);

  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [waText, setWaText] = useState("");
  const [tab, setTab] = useState("whatsapp");

  useEffect(() => {
    setEmailSubject(tpl.emailSubject);
    setEmailBody(tpl.emailBody);
    setWaText(tpl.wa);
  }, [tpl]);

  // German: phone first (verbal consent), then WhatsApp. Otherwise WhatsApp is
  // the preferred channel — default to it whenever a phone number is available, otherwise fall back to whichever channel exists.
  useEffect(() => {
    if (!business) return;
    if (lang === "de") setTab(business.phone ? "phone" : business.email ? "email" : "whatsapp");
    else setTab(business.phone ? "whatsapp" : business.email ? "email" : "whatsapp");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, open, lang]);

  const identityMissing = senderMissing();
  const blockers = sendBlockers({
    hasAudit, readiness, consent: business?.consent, identityMissing, signature: sender,
    needsKit: lang === "de" && !kit && !reportLoading,
  });
  // Hard blockers (empty signature, no German kit) can never be lifted by the override.
  const hardBlocked = blockers.some((b) => HARD_BLOCKERS.includes(b));
  const softBlockers = blockers.filter((b) => !HARD_BLOCKERS.includes(b));
  const sendBlocked = reportLoading || completing || hardBlocked || (blockers.length > 0 && !override);
  const phoneTab = lang === "de" && !!kit;
  const activeTab = tab === "phone" && !phoneTab
    ? (business?.email && !business?.phone ? "email" : "whatsapp")
    : tab;

  const completeNow = async () => {
    if (!onCompleteReport) return;
    const startedGen = dialogGen.current;
    setCompleting(true);
    try {
      const res = await onCompleteReport(business);
      if (dialogGen.current !== startedGen) return;
      setReportUrl(res.url);
      setTop3(res.top3 || null);
      setReadiness(res.readiness || null);
      setEstimatedCost(res.estimatedCostUsd ?? null);
      toast.success(res.readiness?.ready ? "Rapor hazır" : "Bazı ölçümler hâlâ eksik");
    } catch {
      if (dialogGen.current === startedGen) toast.error("Eksik ölçümler tamamlanamadı");
    } finally {
      setCompleting(false);
    }
  };

  if (!business) return null;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} kopyalandı`);
  };

  const mailto = business.email
    ? `mailto:${business.email}?subject=${encodeURIComponent(
        emailSubject
      )}&body=${encodeURIComponent(emailBody)}`
    : null;
  const wa = waLink(business.phone, waText);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="message-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Mesaj Taslağı · {business.name}</DialogTitle>
          <DialogDescription>
            {city ? `${city} · ` : ""}
            {business.has_website ? "Web sitesi var" : "LEAD · web sitesi yok"}
            {hasAudit && ` · Dijital Puan: ${business.digital_score}/100`} — hazır taslağı düzenleyip gönder.
          </DialogDescription>
        </DialogHeader>

        {hasAudit && !business.report_token && (
          <div className="flex items-center gap-2 rounded-md bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
            {reportLoading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rapor linki hazırlanıyor…
              </>
            ) : reportUrl ? (
              <>Rapor linki mesaja eklendi.</>
            ) : (
              <>Rapor linki oluşturulamadı — mesaj rapora referans vermeden hazırlandı.</>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Adınız / Firma</Label>
            <Input
              value={sender}
              onChange={(e) => onSenderChange(e.target.value)}
              placeholder="Örn: Ahmet – WebStudio"
              data-testid="sender-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Dil</Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="message-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="de">Almanca (DE)</SelectItem>
                <SelectItem value="tr">Türkçe (TR)</SelectItem>
                <SelectItem value="en">İngilizce (EN)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasAudit && readiness && (!readiness.ready || readiness.stale) && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="readiness-panel">
            {!readiness.ready && (
              <p className="font-medium">
                Rapor eksik: {readiness.missing.map((m) => READINESS_FIELD_LABELS[m] || m).join(", ")}
              </p>
            )}
            {readiness.stale && (
              <p>Ölçüm {readiness.audited_days_ago} gün önce yapılmış — tazelemek ücretsiz (yeni ölçüm tarihi rapora yansır).</p>
            )}
            {readiness.not_applicable?.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-amber-900/70">
                {readiness.not_applicable.map((n) => <li key={n.field}>{n.reason}</li>)}
              </ul>
            )}
            {onCompleteReport && (
              <Button size="sm" variant="secondary" className="mt-2" onClick={completeNow} disabled={completing} data-testid="complete-report-button">
                {completing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {readiness.ready ? "Ölçümü tazele" : "Eksik ölçümleri tamamla"}
                {estimatedCost === 0
                  ? " (ücretsiz)"
                  : estimatedCost != null && ` (~$${estimatedCost.toFixed(3).replace(".", ",")} · DataForSEO)`}
              </Button>
            )}
          </div>
        )}

        {blockers.filter((b) => b !== "not_ready").map((b) => (
          <div key={b} className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid={`blocker-${b}`}>
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">
              {b === "no_signature"
                ? "İmza boş — önce “Adınız / Firma” alanını doldur (bu uyarı onay kutusuyla aşılamaz)."
                : BLOCKER_TEXT[b]}
            </span>
            {b === "no_consent" && onConsentChange && (
              <ConsentSelect b={business} onConsentChange={onConsentChange} className="w-[128px] border-amber-300 bg-white" />
            )}
          </div>
        ))}

        {softBlockers.length > 0 && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="override-checkbox" />
            Yukarıdaki uyarılara rağmen bilinçli olarak devam et (riski ben alıyorum)
          </label>
        )}

        {lang === "de" && kit && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Mesaj türü</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="message-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first">İlk mesaj (sözlü evetten sonra)</SelectItem>
                  <SelectItem value="noOpen">Takip — rapor AÇILMADIYSA</SelectItem>
                  <SelectItem value="opened">Takip — rapor AÇILDI, alınmadı</SelectItem>
                </SelectContent>
              </Select>
              {kind !== "first" && <p className="text-[11px] text-muted-foreground">Bir kişiye en fazla bir takip. Üçüncü mesaj yok.</p>}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Varyant</Label>
              <Select value={variantChoice || kit.variant} onValueChange={setVariantChoice}>
                <SelectTrigger data-testid="message-variant-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VARIANTS.map((v) => <SelectItem key={v} value={v}>{VARIANT_LABELS[v]}</SelectItem>)}
                </SelectContent>
              </Select>
              {variantChoice && variantChoice !== kit.variant && (
                <p className="text-[11px] text-muted-foreground" data-testid="variant-fallback-note">
                  “{VARIANT_LABELS[variantChoice]}” için veri eksik — “{VARIANT_LABELS[kit.variant]}” kullanıldı.
                </p>
              )}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setTab} className="mt-1">
          <TabsList className={lang === "de" && kit ? "grid w-full grid-cols-3" : "grid w-full grid-cols-2"}>
            {lang === "de" && kit && (
              <TabsTrigger value="phone" data-testid="tab-phone">
                <Phone className="h-4 w-4 mr-1.5" /> Telefon
                {business.phone && <span className="ml-1.5 text-[10px] text-muted-foreground">· önce ara</span>}
              </TabsTrigger>
            )}
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
              <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
              {business.phone && <span className="ml-1.5 text-[10px] text-muted-foreground">· önerilen</span>}
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">
              <Mail className="h-4 w-4 mr-1.5" /> E-posta
            </TabsTrigger>
          </TabsList>

          {lang === "de" && kit && (
            <TabsContent value="phone" className="space-y-3 mt-3">
              <Label className="text-xs">Açılış metni (30–45 sn) — önce ara, sözlü “evet”i al</Label>
              <Textarea value={kit.phone} readOnly rows={12} data-testid="phone-script" />
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => copy(kit.phone, "Telefon metni")} data-testid="copy-phone-button">
                  <Copy className="h-4 w-4" /> Kopyala
                </Button>
                {business.phone && <span className="text-xs text-muted-foreground">{business.phone}</span>}
              </div>
            </TabsContent>
          )}

          <TabsContent value="whatsapp" className="space-y-3 mt-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Mesaj</Label>
              <Textarea
                value={waText}
                onChange={(e) => setWaText(e.target.value)}
                rows={6}
                disabled={reportLoading}
                data-testid="wa-textarea"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => copy(waText, "Mesaj")}
                disabled={sendBlocked || !waText}
                data-testid="copy-wa-button"
              >
                <Copy className="h-4 w-4" /> Kopyala
              </Button>
              <Button
                asChild
                disabled={sendBlocked || !wa}
                className="bg-[hsl(142,60%,38%)] hover:bg-[hsl(142,60%,32%)]"
              >
                <a href={sendBlocked ? undefined : wa || undefined} target="_blank" rel="noreferrer" data-testid="open-wa-link">
                  <MessageCircle className="h-4 w-4" /> WhatsApp'ta Aç
                </a>
              </Button>
              {!business.phone && (
                <span className="text-xs text-muted-foreground">Telefon numarası yok</span>
              )}
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-3 mt-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Konu</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} disabled={reportLoading} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Mesaj</Label>
              <Textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={9}
                disabled={reportLoading}
                data-testid="email-body-textarea"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => copy(`${emailSubject}\n\n${emailBody}`, "E-posta")}
                disabled={sendBlocked || !emailBody}
                data-testid="copy-email-button"
              >
                <Copy className="h-4 w-4" /> Kopyala
              </Button>
              <Button asChild disabled={sendBlocked || !mailto}>
                <a href={sendBlocked ? undefined : mailto || undefined} data-testid="send-email-link">
                  <Send className="h-4 w-4" /> E-posta Aç
                </a>
              </Button>
              {!business.email && (
                <span className="text-xs text-muted-foreground">
                  E-posta yok — önce "E-postaları Bul"u çalıştırın
                </span>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
